// src/components/BottomNav.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Banknote,
  ArrowRightLeft,
  FileText,
  User,
} from 'lucide-react';

export default function BottomNav() {
  const location = useLocation();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // --- تشخیص باز شدن کیبورد (فقط در موبایل) ---
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    const originalHeight = window.innerHeight;
    let currentHeight = originalHeight;

    const handleResize = () => {
      const newHeight = window.innerHeight;
      const diff = Math.abs(newHeight - currentHeight);

      if (diff > 120) {
        setIsKeyboardOpen(newHeight < currentHeight);
        currentHeight = newHeight;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- مخفی‌سازی فقط در چت یا کیبورد ---
  const isChatPage = location.pathname === '/chat_page';
  const shouldHide = isChatPage || isKeyboardOpen;

  const navItems = [
    { to: '/', icon: <Home className="w-6 h-6" />, label: 'خانه' },
    { to: '/deposit-withdraw_page', icon: <Banknote className="w-6 h-6" />, label: 'واریز' },
    { to: '/trade_page', icon: <ArrowRightLeft className="w-6 h-6" />, label: 'معامله' },
    { to: '/transactions_page', icon: <FileText className="w-6 h-6" />, label: 'تراکنش' },
    { to: '/profile_page', icon: <User className="w-6 h-6" />, label: 'پروفایل' },
  ];

  if (shouldHide) return null;

  return (
    <AnimatePresence>
      <motion.nav
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="
          fixed 
          bottom-0 
          left-0 
          right-0 
          bg-white/95 
          backdrop-blur-xl 
          border-t 
          border-gray-200 
          shadow-2xl 
          flex 
          justify-around 
          py-3 
          z-50 
          font-vazir
          pb-[env(safe-area-inset-bottom,0px)]  /* فاصله از پایین در iOS */
        "
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        }}
      >
        {navItems.map((item, index) => {
          const isActive = location.pathname === item.to;

          return (
            <motion.div
              key={item.to}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.05, type: "spring", stiffness: 300 }}
              className="flex-1"
            >
              <Link
                to={item.to}
                className={`flex flex-col items-center justify-center gap-1 transition-all duration-300 ${
                  isActive ? 'text-indigo-600' : 'text-gray-500'
                }`}
              >
                <motion.div
                  animate={{
                    scale: isActive ? 1.2 : 1,
                    y: isActive ? -8 : 0,
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="relative"
                >
                  {item.icon}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-indigo-600 rounded-full"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500 }}
                    />
                  )}
                </motion.div>
                <motion.span
                  animate={{
                    scale: isActive ? 1.1 : 1,
                  }}
                  className="text-xs font-medium"
                >
                  {item.label}
                </motion.span>
              </Link>
            </motion.div>
          );
        })}
      </motion.nav>
    </AnimatePresence>
  );
}